import { QueryObserver, QueryObserverOptions } from '@tanstack/query-core';

import { UserProperty } from '@futrli-sdk/users';

import { checkCurrentUser } from '../../shared-services/src/users/api';
import { queryClient } from '../core/queryClient';

export type CurrentUserProfile = Awaited<ReturnType<typeof checkCurrentUser>>;
export type CurrentUserResult = CurrentUserProfile & { properties?: UserProperty[] };
type CurrentUserQueryOptions = Omit<QueryObserverOptions<CurrentUserProfile>, 'queryKey' | 'queryFn'>;

const CURRENT_USER_STALE_TIME = 1_800_000;

export const currentUserQueryKey = () => ['current-user', 'profile'] as const;

/**
 * Captures the profile query so delayed work can check whether it still belongs to the same session.
 * Call after starting a profile read, before awaiting it. This does not fetch or create a query.
 * Query identity also detects logout and re-login as the same user; refetching keeps the guard valid.
 */
export const createCurrentUserQueryGuard = (): (() => boolean) => {
  const cache = queryClient.getQueryCache();
  const query = cache.find({ exact: true, queryKey: currentUserQueryKey() });

  return () => Boolean(query && cache.get(query.queryHash) === query);
};

export const currentUserQueryOptions = () => ({
  gcTime: Infinity,
  queryFn: () => checkCurrentUser(),
  queryKey: currentUserQueryKey(),
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: false as const,
  staleTime: CURRENT_USER_STALE_TIME
});

export const clearCurrentUserQueryCache = (): Promise<void> =>
  queryClient.invalidateQueries({ exact: true, queryKey: currentUserQueryKey() });

export const ensureCurrentUserData = (): Promise<CurrentUserProfile> =>
  queryClient.fetchQuery(currentUserQueryOptions());

export const refreshCurrentUserData = (): Promise<CurrentUserProfile> =>
  queryClient.fetchQuery({ ...currentUserQueryOptions(), staleTime: 0 });

export const currentUserQuery = (options?: CurrentUserQueryOptions): QueryObserver<CurrentUserProfile> =>
  new QueryObserver<CurrentUserProfile>(queryClient, {
    ...currentUserQueryOptions(),
    ...options
  });
